/**
 * ============================================================================
 * EXECUTIVE SUMMARY TAB — renders N MetricTableSections from config.
 * ============================================================================
 *
 *
 * This is the content for tabs whose type = 'table-sections' in the view
 * config JSON. Each section is an independent MetricTableSection with its own
 * Period, search, and pagination — exactly as the Figma shows multiple
 * network summary tables stacked per network page.
 *
 * FROM THE FIGMA (scrolling down within a network's Executive Summary tab):
 *   ▾ Nova Network Summary                    (section 1)
 *   ▾ Nova Network Summary - On Time Delivery %   (section 2)
 *   ▾ (possibly more sections per config)
 *
 * EACH SECTION:
 * - Gets its config from the JSON (title, owner, goal, metrics list).
 * - Renders independently — the whole point of per-section local state.
 * - Is collapsible: users in the WBR can focus on just the section they're
 *   discussing.
 */

import React from 'react';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import { MetricTableSection } from 'src/components/table/MetricTableSection';
import { DEMAND_CATEGORY_IDS } from 'src/config/demandCategories';
import { MetricTableSectionConfig } from 'src/types';
import { useAppSelector } from 'src/app/store';

interface ExecutiveSummaryTabProps {
  sections?: MetricTableSectionConfig[];
}

/**
 * Which metric kinds each configured section shows. The view config's metric
 * name lists are honoured when they match the API's metric names; the type
 * split keeps volumes and OTD-% sections distinct regardless.
 */
const SECTION_METRIC_TYPES: Record<string, Array<'percentage' | 'volume' | 'count'>> = {
  summary: ['volume', 'count'],
  otd: ['percentage'],
};

/** "All Networks - On Time Delivery %" rollup (R10.3) — aggregated across ALL networks. */
const ALL_NETWORKS_OTD_CONFIG: MetricTableSectionConfig = {
  id: 'otd-all-networks',
  title: 'All Networks - On Time Delivery %',
  metrics: [],
};

export const ExecutiveSummaryTab: React.FC<ExecutiveSummaryTabProps> = ({ sections = [] }) => {
  // Live/Snapshot comes from the page-level toggle: snapshot re-queries the
  // frozen WBR capture and enables the callout column (the snapshot is the
  // version discussed in the WBR meeting).
  const dataMode = useAppSelector((state) => state.dashboard.dataMode);
  const isSnapshot = dataMode === 'snapshot';

  // R10: the QuickSight-parity checkbox row decides which networks' section
  // groups render, stacked in canonical order. Toggling recomputes from the
  // cached rowset — no refetch.
  const selectedDemandCategories = useAppSelector((state) => state.dashboard.selectedDemandCategories);
  const showAllNetworksOtd = useAppSelector((state) => state.dashboard.showAllNetworksOtd);
  const visibleDemandCategories = DEMAND_CATEGORY_IDS.filter((id) => selectedDemandCategories.includes(id));

  return (
    <SpaceBetween direction="vertical" size="l">
      {/* Checkbox row renders at PAGE level above the filters (QuickSight
          order) — see ExecutiveSummaryPage. This tab only reads the state. */}
      {showAllNetworksOtd && (
        <MetricTableSection
          key="otd-all-networks"
          config={ALL_NETWORKS_OTD_CONFIG}
          metricTypes={['percentage']}
          isSnapshot={isSnapshot}
          network="all"
          respectsMetricGroup={false}
        />
      )}

      {visibleDemandCategories.map((networkId) =>
        sections.map((section) => {
          // DSL Coverage exists only on the snapshot sheet (QuickSight
          // parity: collection_owner is frozen at capture time — spec
          // dsl-coverage-snapshot R1).
          if (section.id === 'dsl-coverage' && !isSnapshot) return null;
          return (
            <MetricTableSection
              key={`${networkId}-${section.id}`}
              config={section}
              metricTypes={SECTION_METRIC_TYPES[section.id]}
              isSnapshot={isSnapshot}
              network={networkId}
              // DSL Coverage rows derive from collectionOwner, not the catalog.
              rowSource={section.id === 'dsl-coverage' ? 'dslCoverage' : 'catalog'}
              // Volumes: per-row Owner/Goal (each metric has its own owner).
              // OTD/DSL: one owner for the whole section, shown in the header.
              showOwnerColumns={section.id === 'summary'}
              // Metric Group scopes the volume summary only — the OTD and DSL
              // tables have fixed rows in the live dashboard.
              respectsMetricGroup={section.id === 'summary'}
            />
          );
        }),
      )}
    </SpaceBetween>
  );
};
